import React, { useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navigation from '../components/Navigation';
import LiveGamesSidebar from '../components/LiveGamesSidebar';
import { teams } from '../data/teams';
import { useStandings } from '../hooks/useStandings';

interface Team {
  id: string;
  name: string;
  color: string;
  league: string;
  wins: number;
  losses: number;
  city: string;
  venue: string;
}

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const { standings, getTeamStanding } = useStandings();

  // Merge team data with real-time standings
  const teamsWithStandings = useMemo(() => {
    return teams.map(team => {
      const standing = getTeamStanding(team.id);
      const mergedTeam = {
        ...team,
        wins: standing ? standing.wins : 0,
        losses: standing ? standing.losses : 0
      };
      return mergedTeam;
    });
  }, [standings, getTeamStanding]);
  
  // Debug: Check if standings are being used
  useEffect(() => {
    const standingsCount = Object.keys(standings).reduce((sum, league) => sum + (standings[league]?.length || 0), 0);
    const sampleTeam = teams[0];
    if (sampleTeam) {
      const standing = getTeamStanding(sampleTeam.id);
      console.log(`🔍 [STANDINGS DEBUG] Total standings: ${standingsCount} | Sample: ${sampleTeam.name} = ${standing ? `${standing.wins}-${standing.losses}` : 'no standing'}`);
    }
  }, [standings, getTeamStanding]);

  // Filter teams by league with real-time standings
  const nationalTeams = useMemo(() => 
    teamsWithStandings.filter(team => team.league === 'National League'),
    [teamsWithStandings]
  );
  
  const corporateTeams = useMemo(() => 
    teamsWithStandings.filter(team => team.league === 'Corporate League'),
    [teamsWithStandings]
  );

  const TeamTable: React.FC<{ title: string; teams: Team[] }> = ({ title, teams }) => (
    <div className="table--team">
      <style>
        {`.trow.team-record .team-color--overlay {
          transition: opacity 0.2s ease;
        }
        /* Mobile: keep colored circles always visible by hiding the grey overlay */
        @media (max-width: 768px) {
          .trow.team-record .team-color--overlay {
            opacity: 0 !important;
          }
          .table--team:hover .trow.team-record:not(:hover) {
            opacity: 1 !important;
          }
        }
        /* Desktop: hover effects */
        @media (hover: hover) and (min-width: 769px) {
          .trow.team-record:hover .team-color--overlay {
            opacity: 0;
          }
          .table--team:hover .trow.team-record:not(:hover) {
            opacity: 0.4;
            transition: opacity 0.2s ease;
          }
          .table--team:hover .trow.team-record:not(:hover) .team-name {
            transition: opacity 0.2s ease;
          }
        }`}
      </style>
      <div className="thead">
        <div>{title}</div>
      </div>
      {teams.map((team, index) => (
        <div 
          key={team.id} 
          className="trow team-record w-inline-block"
          onClick={() => navigate(`/rosters?team=${team.id}`)}
          style={{ cursor: 'pointer' }}
        >
          <div className="team-icon">
            <div className="team-color--container">
              <div className="team-color--overlay"></div>
              <table style={{ backgroundColor: team.color }} className="team-color"></table>
            </div>
            <div className="team-name">{team.name}</div>
          </div>
          <div className="team-record">
            <div>{team.wins}</div>
            <div>-</div>
            <div>{team.losses}</div>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <>
      <Navigation />
      
      <div className="container--interior">
        <LiveGamesSidebar />

        <div className="interior-wrapper">
          <div className="hero-header--wrapper">
            <div className="h1--hero-wrapper">
              <img src="/images/league.svg" loading="lazy" alt="" className="image" />
            </div>
          </div>
          
          <div className="hero-subtitle">NatiCorp Mech Sports Enterprise</div>
          
          <div className="hero-subblock">
            <div className="season--callout">
              <div className="season-50-50">
                <div className="season-content">
                  <div className="overline">Now Playing</div>
                  <h2 className="h2--season">Season 1</h2>
                </div>
                <svg viewBox="0 0 200 50" preserveAspectRatio="none" className="diagonal">
                  <rect x="0.5" y="0.5" width="199" height="49" fill="none" stroke="#666" strokeWidth="1" vectorEffect="non-scaling-stroke"></rect>
                  <line x1="200" y1="0" x2="0" y2="50" stroke="#666" strokeWidth="1" vectorEffect="non-scaling-stroke"></line>
                </svg>
              </div>
            </div>
            
            <div className="what--callout">
              <div className="what-callout--styling"></div>
              <div className="plus-callout">
                <div className="w-embed">
                  <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 10 10" fill="none">
                    <path d="M6 4H10V6H6V10H4V6H0V4H4V0H6V4Z" fill="white"></path>
                  </svg>
                </div>
                <h4>What is this?</h4>
              </div>
              <div className="what-callout--content">
                <p>Welcome to the premier mech sports league featuring the world's top pilots and cutting-edge mechanical warfare. Each match showcases tactical combat between elite teams across National and Corporate divisions.</p>
                <a href="#desc" className="link--cmd w-inline-block">
                  <div className="link-prefix">$cd/</div>
                  <div className="link--label">Learn More</div>
                  <div style={{ opacity: 0 }} className="link-cursor"></div>
                </a>
              </div>
            </div>
          </div>

          <div className="home-content--wrapper">
            <div className="column--teams">
              <TeamTable title="National League" teams={nationalTeams} />
              <TeamTable title="Corporate League" teams={corporateTeams} />
            </div>
            
            <div id="w-node-_1cbbe3dd-80f3-e3ec-d4af-673638b9ac23-ae6402ce" className="column--schedule">
              <div className="table--schedule">
                <div className="thead">
                  <div>Play Schedule</div>
                </div>
                <div className="trow team-record current">
                  <div>11 10-13</div>
                  <div>Season Play</div>
                </div>
                <div className="trow team-record notcurrent">
                  <div>11 14</div>
                  <div>Playoffs</div>
                </div>
                <div className="trow team-record notcurrent">
                  <div>11 15</div>
                  <div>Finals</div>
                </div>
                <div className="trow team-record last notcurrent">
                  <div>11 16</div>
                  <div>Restructuring</div>
                </div>
              </div>
              
              <div className="restruct-wrapper">
                <div className="restruct-scroller">
                  <div className="restruct-content">
                    <div className="restruct-label">Current Rules</div>
                  </div>
                  <div className="rule">
                    <div className="rule-name">
                      <div className="w-embed">
                        <svg xmlns="http://www.w3.org/2000/svg" width="7" height="7" viewBox="0 0 7 7" fill="none">
                          <path d="M4 3H7V4H4V7H3V4H0V3H3V0H4V3Z" fill="white"></path>
                        </svg>
                      </div>
                      <div>Combat Restrictions</div>
                    </div>
                    <p className="rule-text">No lethal ammunition allowed. Scoring system based on targeting accuracy and tactical positioning.</p>
                  </div>
                  <div className="rule">
                    <div className="rule-name">
                      <div className="w-embed">
                        <svg xmlns="http://www.w3.org/2000/svg" width="7" height="7" viewBox="0 0 7 7" fill="none">
                          <path d="M4 3H7V4H4V7H3V4H0V3H3V0H4V3Z" fill="white"></path>
                        </svg>
                      </div>
                      <div>Mech Classifications</div>
                    </div>
                    <p className="rule-text">Teams must field balanced rosters across Support, Sniper, Assault, Commander, and Defender classes.</p>
                  </div>
                  <div className="rule">
                    <div className="rule-name">
                      <div className="w-embed">
                        <svg xmlns="http://www.w3.org/2000/svg" width="7" height="7" viewBox="0 0 7 7" fill="none">
                          <path d="M4 3H7V4H4V7H3V4H0V3H3V0H4V3Z" fill="white"></path>
                        </svg>
                      </div>
                      <div>Arena Regulations</div>
                    </div>
                    <p className="rule-text">All matches conducted in regulation 337x157 meter combat arenas with standardized zone configurations.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="divider"></div>
          
          <div id="desc" className="desc-hero">
            <svg id="w-node-f433c9cd-4f6c-3118-00ef-5f2b592d2dd6-ae6402ce" viewBox="0 0 200 50" preserveAspectRatio="none" className="diagonal">
              <rect x="0.5" y="0.5" width="199" height="49" fill="none" stroke="#666" strokeWidth="1" vectorEffect="non-scaling-stroke"></rect>
              <line x1="200" y1="0" x2="0" y2="50" stroke="#666" strokeWidth="1" vectorEffect="non-scaling-stroke"></line>
            </svg>
            <h1 className="h2--hero">What is <br />LEAGUE?</h1>
          </div>
          
          <div className="paragraph w-richtext">
            <p>The premier mech combat sports league of 2077, featuring the world's most advanced pilot-operated combat mechs in regulated tournament combat. Teams compete across two divisions - National League representing global cities, and Corporate League featuring the leading technology corporations.</p>
            <h3>Combat System</h3>
            <p>Matches are scored using a sophisticated targeting system where pilots earn 10 points for close-range precision hits and 25 points for long-range tactical strikes. Victory requires both superior piloting skill and strategic team coordination across five distinct mech classifications.<br /><br />Each match is contested in regulation combat arenas featuring multiple tactical zones, from close-quarters paint zones to long-range deep strike areas. Pilots must master their mech's unique capabilities while adapting to real-time battlefield conditions.</p>
          </div>
          
          <p className="copyright"><span className="copyright-icon">© </span>2077 Neo Vegas CORP Trust</p>
          <p className="disclaimer">* Nullam quis risus eget urna mollis ornare vel eu leo. Nulla vitae elit libero, a pharetra augue. Fusce dapibus, tellus ac cursus commodo, tortor mauris condimentum nibh, ut fermentum massa justo sit amet risus.</p>
        </div>
      </div>
    </>
  );
};

export default HomePage; 